/**
 * AppHeader - Clean, focused navigation header
 *
 * Design principles (from master UX prompt):
 * - Norman: Clear affordances, minimal cognitive load
 * - Krug: Don't make users think - obvious navigation
 * - Wroblewski: Reduce visual noise, clear hierarchy
 * - Seiden: Make desired actions easier and clearer
 *
 * Structure: Logo | Breadcrumb | Primary Nav | Status | Utilities
 */

import { useState, useRef, useEffect } from 'react';
import { isFeatureEnabled, FEATURES } from '../config/features';

// Icons
const HelpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

// View name mapping for breadcrumb display
const VIEW_NAMES = {
  work: 'My Work',
  projects: 'Projects',
  team: 'Team',
  printers: 'Printers',
  reports: 'Reports',
  schedule: 'Schedule Board',
  production: 'Production View',
  'pm-command': 'PM Command Center',
  'work-orders': 'Work Orders',
  'work-order-review': 'Work Order Review',
  'admin-tickets': 'Admin Tickets',
  'admin-users': 'User Management',
  'data-quality': 'Data Quality',
  'feedback-dashboard': 'Feedback',
  'work-instructions': 'Work Instructions',
  'session-history': 'Session History',
  'pa-queue': 'PA Queue',
  'assembly-queue': 'Assembly Queue',
  'shipping-queue': 'Shipping Queue',
  'notification-admin': 'Notifications',
};

export default function AppHeader({
  // User & role info
  user,
  userEmail,
  isPM,
  isAdmin,
  displayName,

  // Status indicators
  activeSessionCount = 0,
  batchSessionCount = { groupCount: 0, jobCount: 0 },

  // Current view for breadcrumb
  currentView = 'work',
  onNavigateHome,

  // Modal/view toggles (passed through for menu items)
  setShowProjectSchedule,
  setShowTeamView,
  setShowPADashboard,
  setShowPAQueue,
  setShowAssemblyQueue,
  setShowShippingQueue,
  setShowProductionSchedule,
  setShowProductionView,
  setShowWorkOrderGenerator,
  setShowPMCommandCenter,
  setShowPMInterface,
  setShowWorkOrderDemo,
  setShowReportsDashboard,
  setShowFeedbackDashboard,
  setShowAdminTickets,
  setShowAdminUserManagement,
  setShowWorkInstructions,
  setShowDataQuality,
  setShowNotificationAdmin,
  setShowFeedbackModal,
  onReprintRemake,

  // Notification component (rendered in utilities)
  NotificationSettings,

  // Staging indicator
  isStaging = false,
}) {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine user role label
  const getRoleLabel = () => {
    const roles = [];
    if (isAdmin) roles.push('Admin');
    if (isPM) roles.push('PM');
    if (roles.length === 0) roles.push('Production');
    return roles.join(' • ');
  };

  // Handle navigation - close all views first, then open target
  const navigateTo = (viewSetter) => {
    onNavigateHome?.(); // Close all views
    setTimeout(() => viewSetter?.(true), 0); // Open target view
    setShowProfileMenu(false);
  };

  // Primary navigation tabs
  const primaryTabs = [
    { id: 'work', label: 'My Work' },
    { id: 'projects', label: 'Projects' },
    { id: 'team', label: 'Team' },
  ];

  // Check if we're on a secondary view (not one of the primary tabs)
  const isSecondaryView = !['work', 'projects', 'team'].includes(currentView);
  const currentViewName = VIEW_NAMES[currentView] || currentView;

  // Build menu sections based on user role
  const buildMenuSections = () => {
    const sections = [];

    // Production Views section (work center queues)
    const productionItems = [
      { label: 'PA Queue', action: () => navigateTo(setShowPAQueue) },
      { label: 'Assembly Queue', action: () => navigateTo(setShowAssemblyQueue) },
      { label: 'Shipping Queue', action: () => navigateTo(setShowShippingQueue) },
    ];
    sections.push({ title: 'Production Views', items: productionItems });

    // Views section (available to most users)
    const viewItems = [
      { label: 'Printers', action: () => navigateTo(setShowPADashboard) },
      { label: 'Reports', action: () => navigateTo(setShowReportsDashboard) },
    ];

    if (isFeatureEnabled(FEATURES.PRODUCTION_SCHEDULE, userEmail)) {
      viewItems.push({ label: 'Schedule Board', action: () => navigateTo(setShowProductionSchedule) });
    }

    if (isFeatureEnabled(FEATURES.PRODUCTION_FLOOR_VIEW, userEmail)) {
      viewItems.push({ label: 'Production View', action: () => navigateTo(setShowProductionView) });
    }

    sections.push({ title: 'Views', items: viewItems });

    // PM Tools section (PM users only)
    if (isPM) {
      const pmItems = [];

      if (isFeatureEnabled(FEATURES.PM_COMMAND_CENTER, userEmail)) {
        // New PM Interface (primary)
        pmItems.push({ label: 'PM Interface', action: () => navigateTo(setShowPMInterface) });
        pmItems.push({ label: 'Generate Work Order', action: () => navigateTo(setShowWorkOrderGenerator) });
        // Legacy Command Center (for comparison/fallback)
        pmItems.push({ label: 'Command Center (Legacy)', action: () => navigateTo(setShowPMCommandCenter) });
      }

      if (isFeatureEnabled(FEATURES.WORK_ORDER_REVIEW, userEmail)) {
        pmItems.push({ label: 'Work Order Review', action: () => navigateTo(setShowWorkOrderDemo) });
      }

      if (pmItems.length > 0) {
        sections.push({ title: 'PM Tools', items: pmItems });
      }
    }

    // Admin section (admin users only)
    if (isAdmin) {
      const adminItems = [
        { label: 'User Management', action: () => navigateTo(setShowAdminUserManagement) },
        { label: 'Tickets', action: () => navigateTo(setShowAdminTickets) },
        { label: 'Data Quality', action: () => navigateTo(setShowDataQuality) },
        { label: 'Feedback Dashboard', action: () => navigateTo(setShowFeedbackDashboard) },
        { label: 'Work Instructions', action: () => navigateTo(setShowWorkInstructions) },
        { label: 'Notifications', action: () => navigateTo(setShowNotificationAdmin) },
      ];
      sections.push({ title: 'Admin', items: adminItems });
    }

    return sections;
  };

  const menuSections = buildMenuSections();

  return (
    <header className="w-full bg-white border-b border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-4">
        <div className="h-14 flex items-center justify-between">

          {/* LEFT: Logo + Work Center + Breadcrumb */}
          <div className="flex items-center gap-4">
            {/* Logo / Home Button */}
            <button
              onClick={() => onNavigateHome?.()}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              title="Go to My Work"
            >
              <h1 className="text-lg font-bold text-gray-900 tracking-wide">
                3D COLOR
              </h1>
            </button>

            {/* Staging Badge */}
            {isStaging && (
              <span className="px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded">
                STAGING
              </span>
            )}

            {/* Breadcrumb - shows current location */}
            {isSecondaryView && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">/</span>
                <span className="text-gray-700">{currentViewName}</span>
              </div>
            )}
          </div>

          {/* CENTER: Primary Navigation Tabs */}
          <nav className="flex items-center gap-1">
            {primaryTabs.map((tab) => {
              const isActive = currentView === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    onNavigateHome?.();
                    if (tab.id === 'projects') {
                      setTimeout(() => setShowProjectSchedule?.(true), 0);
                    } else if (tab.id === 'team') {
                      setTimeout(() => setShowTeamView?.(true), 0);
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-purple-600 text-gray-900'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-slate-100'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* RIGHT: Status + Utilities */}
          <div className="flex items-center gap-3">

            {/* Reprint/Remake Button - Report issues needing reprints or remakes */}
            <button
              onClick={() => onReprintRemake?.()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-700 hover:text-amber-800 rounded-md transition-colors text-sm font-medium"
              title="Report a reprint or remake needed"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reprint/Remake
            </button>

            {/* Active Sessions Badge */}
            {activeSessionCount > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 border border-green-200 rounded-md">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-xs text-green-600 font-medium">
                  {activeSessionCount} active
                </span>
              </div>
            )}

            {/* Batch Badge */}
            {batchSessionCount.groupCount > 0 && (
              <div
                className="flex items-center gap-1.5 px-2 py-1 bg-purple-100 border border-purple-200 rounded-md"
                title={`${batchSessionCount.jobCount} jobs in ${batchSessionCount.groupCount} batch group(s)`}
              >
                <GridIcon />
                <span className="text-xs text-purple-600 font-medium">
                  {batchSessionCount.groupCount} batch
                </span>
              </div>
            )}

            {/* Notifications */}
            {NotificationSettings && <NotificationSettings />}

            {/* Help */}
            <a
              href="/tracker/help/user-guide.html"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-slate-100 rounded-md transition-colors"
              title="Help & User Guide"
            >
              <HelpIcon />
            </a>

            {/* Profile Menu */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-700 hover:text-gray-900 hover:bg-slate-100 rounded-md transition-colors"
              >
                <div className="w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-900">
                    {displayName?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <ChevronDownIcon />
              </button>

              {/* Profile Dropdown */}
              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-100 border border-gray-200 rounded-lg shadow-xl z-50 max-h-[calc(100vh-80px)] overflow-y-auto">
                  {/* User Info Header */}
                  <div className="px-4 py-3 bg-gray-750 border-b border-gray-200">
                    <div className="font-medium text-gray-900">{displayName || 'User'}</div>
                    <div className="text-xs text-gray-500">{getRoleLabel()}</div>
                  </div>

                  {/* Menu Sections */}
                  {menuSections.map((section, sectionIndex) => (
                    <div key={section.title} className={sectionIndex > 0 ? 'border-t border-gray-200' : ''}>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {section.title}
                      </div>
                      {section.items.map((item) => (
                        <button
                          key={item.label}
                          onClick={item.action}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 hover:text-gray-900 transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  ))}

                  {/* Footer Actions */}
                  <div className="border-t border-gray-200">
                    <a
                      href="/tracker/downloads/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 hover:text-gray-900 transition-colors"
                    >
                      Download Desktop App
                    </a>
                    <button
                      onClick={() => {
                        setShowFeedbackModal?.(true);
                        setShowProfileMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-slate-100 hover:text-gray-900 transition-colors"
                    >
                      Send Feedback
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
